import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import userApi from '@/api/user';
import { userActions } from './userStore';
import * as Sentry from '@sentry/react';

const initialValues = {
  csrfToken: null,
};

const useAuthStore = create()(
  immer(
    persist(() => initialValues, {
      name: 'authStore',
      version: 1,
    }),
  ),
);

const { setState: set } = useAuthStore;

const mfaErrorMessages = {
  mfa_required: 'Enter the code from your authenticator app.',
  invalid_totp: 'That authenticator code is invalid or has already been used.',
  totp_throttled: 'Too many attempts. Wait a moment before trying another code.',
};

export const authActions = {
  login: async loginData => {
    try {
      const { data } = await userApi.login(loginData);
      const { csrf_token: csrfToken } = data;

      set({ csrfToken });
    } catch (error) {
      const code = error?.response?.data?.code;
      return {
        code,
        mfaRequired: code === 'mfa_required',
        error: mfaErrorMessages[code] || error?.response?.data?.detail || 'Unable to log in.',
      };
    }
  },
  register: async registerData => {
    try {
      const { data } = await userApi.register(registerData);
      const { csrf_token: csrfToken } = data;

      set({ csrfToken });
    } catch (error) {
      return {
        error: error?.response?.data?.detail,
      };
    }
  },
  forgotPassword: async forgotPasswordData => {
    try {
      await userApi.forgotPassword(forgotPasswordData);
    } catch (error) {
      return {
        error: error?.response?.data?.detail || 'An error occurred',
      };
    }
  },
  logout: async () => {
    await userApi.logout();
    set({ csrfToken: null });
  },

  isAuthenticated: async () => {
    try {
      const { data } = await userApi.getUserInfo();
      const { csrf_token: csrfToken, email, name } = data;

      userActions.setUserInfo({ email, name });

      set({ csrfToken });

      Sentry.setUser({ email });

      return true;
    } catch {
      return false;
    }
  },
};

export default useAuthStore;
